
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, File, FileText, Image as ImageIcon, Link, Loader2, UploadIcon, X } from 'lucide-react';

interface UploadFieldProps {
  id: string;
  label: string;
  fileUrl: string;
  isUploading: boolean;
  previewType: 'image' | 'document';
  accept?: string;
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onViewExternal?: () => void;
  uploadError?: string;
  onExternalUrl?: (url: string) => void;
  disableUpload?: boolean;
}

const UploadField: React.FC<UploadFieldProps> = ({
  id,
  label,
  fileUrl,
  isUploading,
  previewType,
  accept,
  onFileSelect,
  onRemove,
  onViewExternal,
  uploadError,
  onExternalUrl,
  disableUpload = false
}) => {
  const [previewError, setPreviewError] = useState(false);
  const [externalUrlMode, setExternalUrlMode] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  const getFileNameFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return pathParts[pathParts.length - 1];
    } catch (e) {
      return 'file';
    }
  };
  
  const handleUseExternalUrl = () => {
    if (onExternalUrl && externalUrl) {
      onExternalUrl(externalUrl);
      setExternalUrlMode(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block mb-2">
        {label}
        {disableUpload && <span className="text-xs text-orange-500 ml-1">(External URLs only - upload requires admin rights)</span>}
      </label>
      
      {!fileUrl && !externalUrlMode ? (
        <div className="space-y-2">
          {!disableUpload && onFileSelect && (
            <div className="relative">
              <Input
                id={id}
                type="file"
                accept={accept}
                onChange={onFileSelect}
                disabled={isUploading || disableUpload}
              />
              {uploadError && (
                <div className="flex items-center gap-2 mt-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  <span>{uploadError}</span>
                </div>
              )}
            </div>
          )}
          
          {onExternalUrl && (
            <div className="mt-1 flex justify-end">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setExternalUrlMode(true)}
                className="text-xs"
              >
                <Link className="h-3 w-3 mr-1" />
                {disableUpload ? "Use External URL" : "Use External URL Instead"}
              </Button>
            </div>
          )}
          
          {disableUpload && (
            <p className="text-sm text-gray-500 mt-2">
              Direct uploads are restricted to administrators. Please use an external URL for your {previewType === 'image' ? 'image' : 'document'}.
            </p>
          )}
        </div>
      ) : externalUrlMode ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="Enter URL (https://...)"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleUseExternalUrl} disabled={!externalUrl}>
              <Link className="h-4 w-4 mr-2" />
              Use URL
            </Button>
            <Button variant="outline" onClick={() => setExternalUrlMode(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            {previewType === 'image'
              ? 'Use a publicly accessible image URL (ends with .jpg, .png, etc)'
              : 'Use a publicly accessible document URL (ends with .pdf, .ppt, etc)'}
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between p-2 border rounded-md">
          <div className="flex items-center gap-2">
            {previewType === 'image' ? (
              <div 
                className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden"
                style={{ border: previewError ? '1px solid #eee' : 'none' }}
              >
                {previewError ? (
                  <ImageIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <img 
                    src={fileUrl + `?t=${Date.now()}`} // Add cache-busting parameter
                    alt={label} 
                    className="h-full w-full object-cover"
                    onError={() => setPreviewError(true)}
                  />
                )}
              </div>
            ) : (
              <div className="h-10 w-10 bg-blue-50 rounded flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
            )}
            
            <div className="text-sm overflow-hidden">
              <div className="font-medium truncate max-w-[180px]">
                {getFileNameFromUrl(fileUrl)}
              </div>
              <div className="text-xs text-gray-500">
                {previewType === 'image' ? 'Image' : 'Document'} {previewError ? '(Preview unavailable)' : 'uploaded'}
              </div>
            </div>
          </div>
          
          <div className="flex gap-1">
            {onViewExternal && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={onViewExternal}
              >
                <FileText className="h-4 w-4" />
              </Button>
            )}
            <Button 
              variant="outline" 
              size="icon"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      
      {isUploading && (
        <div className="mt-2 text-sm text-blue-600 flex items-center gap-1">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Uploading...</span>
        </div>
      )}
    </div>
  );
};

export default UploadField;
